/**
 * Marketplace configuration — installer network + Todae equipment shop.
 *
 * Single source of truth for the business constants of the One-Stop-Shop
 * transactional layer (Design → Buy → Install). Values that the operator may
 * tune per market are env-driven with sensible defaults.
 *
 * Currency (decision D3, Faza 4): everything is stored/computed in BASE ZAR;
 * the display currency (ZAR for Africa, derived USD for the USA) is resolved at
 * the display edge via src/context/CurrencyContext.tsx. `formatUSD` is retained
 * only for legacy call sites pending the Faza 6 UI swap.
 */

import { formatCurrency } from '@/lib/currency';
import type { RegionCurrency } from '@/config/currency-region';
import { ENTITIES } from '@/config/platform';

const env = import.meta.env;

/** Share of the gross job value paid out to the installer (rest = platform). */
export const INSTALLER_PAYOUT_SHARE = Number(env.VITE_INSTALLER_PAYOUT_SHARE ?? 0.70);

/** Default job-search radius shown to installers, in km. */
export const DEFAULT_RADIUS_KM = 50;
/** Radius slider bounds (km). */
export const MIN_RADIUS_KM = 5;
export const MAX_RADIUS_KM = 300;

/**
 * Todae equipment store — the separate shop that stocks & ships the BOM.
 *
 * The handoff crosses domains: the cart deep-link lands on the shop's own
 * /cart/import, never on the planner's /todae mount. The origin is the one
 * declared for the shop entity in src/config/platform.ts (VITE_SHOP_ORIGIN
 * points it at a local shop); VITE_TODAE_BASE_URL stays as an explicit
 * override for a staging shop only.
 */
export const TODAE = {
  name: 'Todae',
  baseUrl: (env.VITE_TODAE_BASE_URL ?? ENTITIES.shop.origin).replace(/\/+$/, ''),
  /** Cart handoff endpoint (deep link). The cart payload is base64-JSON in the `cart` param. */
  cartPath: env.VITE_TODAE_CART_PATH ?? '/cart/import',
} as const;

/** Flat-rate shipping estimate tiers in BASE ZAR (excl VAT) until Todae quotes live rates. */
export const SHIPPING_ESTIMATE = {
  freeAbove: 45000, // ≈ R45k order → free delivery
  flat: 900,
  heavy: 2400, // orders with >24 line units (racks, poles, cable drums)
  heavyUnitThreshold: 24,
} as const;

/** Format a base-ZAR amount in the given display currency (ZAR or USD). */
export const formatMoney = (amount: number, currency: RegionCurrency, locale = 'en') =>
  formatCurrency(amount, currency, locale);

/** @deprecated legacy USD formatter — use useCurrency().format (base ZAR in). */
export const formatUSD = (n: number, opts: Intl.NumberFormatOptions = {}) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    ...opts,
  }).format(n);

/** Installer payout for a gross job value (base ZAR), after the TodAidesign margin. */
export const installerPayout = (gross: number) =>
  Math.round(gross * INSTALLER_PAYOUT_SHARE);
